/* ==========================================================================
   Giorgio pairing offer — the product page's #pairModal.

   The dialog itself is rendered server-side by the section, so this only
   decides when to open it, drives the picker, and commits the choice.

   WHEN IT OPENS
   After the product is added, once per page load, and only if the bag holds
   none of the products being offered. The "asked already" flag lives in memory
   on purpose — the mockup's own note:

       "Seen-state is in-memory only: one ask per page load, cleared by a
        reload. Web storage was making a single decline permanent, which turned
        a courtesy into a lockout."

   It listens for the cart to change rather than for the button to be clicked,
   so the product form and the no-default-variant gating built on top of it are
   never touched. The pairing is added on its own request; the drawer re-renders
   from the response the same way the rail's does.
   ========================================================================== */
(function () {
  'use strict';

  // Two copies of this file on one page meant two click handlers, and the
  // dropdown's classList.toggle('open') ran twice per click — it opened and
  // shut in the same tick. Only the first copy wires anything up.
  if (window.__gioPairingOffer) return;
  window.__gioPairingOffer = true;

  var SECTIONS = ['cart-drawer', 'cart-icon-bubble'];
  // PUB_SUB_EVENTS is a top-level `const` in Dawn's constants.js. That makes it a
  // global binding but NOT a property of window, so it has to be read as a bare
  // identifier — window.PUB_SUB_EVENTS is undefined and every guard on it fails.
  var EVENTS = typeof PUB_SUB_EVENTS !== 'undefined' ? PUB_SUB_EVENTS : null;
  var pub = typeof publish === 'function' ? publish : null;
  var sub = typeof subscribe === 'function' ? subscribe : null;

  var asked = false;
  var checkoutPending = null;
  var staged = []; // { variantId, name, price, qty }


  /* ---- the drawer waits its turn ----------------------------------------
     The offer is an interruption, so it has to arrive BEFORE the bag, not on
     top of it. Dawn opens the drawer from product-form's own .then(), which
     runs right after publish() — and publish() calls this subscriber first.

     So the drawer's open() is pocketed for the length of the interruption. The
     section swap still happens (the count, the reward bar, the rail all update
     underneath); only the opening waits. Nothing about the product form or the
     add itself is touched.                                                   */
  function deferDrawer() {
    var d = document.querySelector('cart-drawer');
    if (!d || d.gioDeferred) return;
    d.gioDeferred = true;
    d.gioOpen = d.open;
    d.gioWanted = false;
    d.open = function () {
      d.gioWanted = true;
    };
  }

  function releaseDrawer(openNow) {
    var d = document.querySelector('cart-drawer');
    if (!d || !d.gioDeferred) return;
    d.open = d.gioOpen;
    d.gioDeferred = false;
    var wanted = d.gioWanted;
    d.gioWanted = false;
    if (openNow && wanted) d.open();
  }

  /* ---- which dialog ------------------------------------------------------
     A page can carry several offers, each a block with its own rules. The one
     that answers the current cart is chosen once and then stays chosen for the
     rest of the page load, so a shopper is never handed a second dialog after
     declining the first. */
  var active = null;

  function allOffers() {
    return Array.prototype.slice.call(document.querySelectorAll('.gio-pairing-offer'));
  }

  function num(el, attr) {
    var raw = (el.getAttribute(attr) || '').trim();
    if (!raw) return null;
    var n = parseFloat(raw);
    return isNaN(n) ? null : n;
  }

  function cartTotals(cartData) {
    // cart/add.js returns the line just added, cart.js returns the whole cart.
    if (cartData && typeof cartData.item_count === 'number') {
            return { count: cartData.item_count, cents: cartData.original_total_price || cartData.total_price || 0, items: cartData.items || [] };
    }
    return null;
  }

  function fits(el, totals) {
    if (el.getAttribute('data-gpo-require-missing') === '1' && bagHasOffer(el)) return false;
    if (!totals) {
      // No cart snapshot yet: only rules that need one are skipped.
      return !el.getAttribute('data-gpo-need-product') &&
             num(el, 'data-gpo-min-qty') === null &&
             num(el, 'data-gpo-max-qty') === null &&
             num(el, 'data-gpo-min-total') === null &&
             num(el, 'data-gpo-max-total') === null;
    }
    var minQty = num(el, 'data-gpo-min-qty');
    var maxQty = num(el, 'data-gpo-max-qty');
    var minTotal = num(el, 'data-gpo-min-total');
    var maxTotal = num(el, 'data-gpo-max-total');
    if (maxTotal !== null && totals.cents > maxTotal * 100) return false;
    if (minQty !== null && totals.count < minQty) return false;
    if (maxQty !== null && totals.count > maxQty) return false;
    if (minTotal !== null && totals.cents < minTotal * 100) return false;
    if (!needsBagItem(el, totals.items)) return false;
    return true;
  }

  /* "Only if the bag holds exactly one of…": the bag must carry exactly ONE
     unit of the chosen product, in one of the listed packagings (for the
     cookie page: one dozen or one gift box of Italian Cookies). */
  function needsBagItem(el, items) {
    var pid = (el.getAttribute('data-gpo-need-product') || '').trim();
    if (!pid) return true;
    var packs = (el.getAttribute('data-gpo-need-variant') || '')
      .split(',')
      .map(function (s) { return s.trim().toLowerCase(); })
      .filter(Boolean);
    var units = 0;
    var allMatch = true;
    (items || []).forEach(function (it) {
      if (String(it.product_id) !== pid) return;
      units += it.quantity || 0;
      if (packs.length) {
        var t = String(it.variant_title || '').toLowerCase();
        if (!packs.some(function (p) { return t.indexOf(p) > -1; })) allMatch = false;
      }
    });
    return units === 1 && allMatch;
  }

  function chooseOffer(cartData, moment) {
    if (active) return active;
    var totals = cartTotals(cartData);
    var offers = allOffers();
    for (var i = 0; i < offers.length; i++) {
      if ((offers[i].getAttribute('data-gpo-trigger') || 'checkout') !== moment) continue;
      if (fits(offers[i], totals)) {
        active = offers[i];
        return active;
      }
    }
    return null;
  }

  function modal() {
    return active;
  }

  function picker() {
    var el = modal();
    return el ? el.querySelector('[data-gpo-pick-wrap]') : null;
  }

  function money(cents) {
    var n = cents / 100;
    return '$' + (n % 1 === 0 ? n.toFixed(0) : n.toFixed(2));
  }

  function applyCartResponse(parsed) {
    var drawer = document.querySelector('cart-drawer');
    if (drawer && typeof drawer.renderContents === 'function') {
      drawer.renderContents(parsed);
    } else {
      window.location.reload();
      return;
    }
    if (pub && EVENTS) {
      pub(EVENTS.cartUpdate, {
        source: 'giorgio-pairing-offer',
        cartData: parsed,
      });
    }
  }

  /* ---- the offer -------------------------------------------------------- */

  /* Someone who already has the coffee has answered the question. The answer has
     to be known SYNCHRONOUSLY — one fetch and the drawer has already opened — so
     the bag's product ids are rendered into the section at page load and kept up
     to date from every cart response seen since. */
  var known = null;

  function knownIds() {
    if (known) return known;
    var el = allOffers()[0];
    known = {};
    if (el) {
      (el.getAttribute('data-cart-ids') || '').split(',').forEach(function (id) {
        if (id) known[id] = true;
      });
    }
    return known;
  }

  function remember(cart) {
    var ids = knownIds();
    if (!cart) return;
    if (cart.items) {
      cart.items.forEach(function (i) {
        ids[String(i.product_id)] = true;
      });
    } else if (cart.product_id) {
      ids[String(cart.product_id)] = true;
    }
  }

  function bagHasOffer(el) {
    if (!el) el = modal();
    if (!el) return true;
    var offers = (el.getAttribute('data-offer-ids') || '').split(',').filter(Boolean);
    if (!offers.length) return true;
    var ids = knownIds();
    return offers.some(function (id) {
      return ids[id];
    });
  }

  function maybeOffer(cartData, moment) {
    remember(cartData);
    if (asked) return false;
    var el = chooseOffer(cartData, moment || 'add_to_cart');
    if (!el) return false;
    asked = true;
    deferDrawer();
    open();
    return true;
  }

  /* ---- the dialog ------------------------------------------------------- */

  function open() {
    var el = modal();
    if (!el) return;
    el.hidden = false;
    render();
    var trigger = el.querySelector('[data-gpo-toggle]');
    if (trigger) trigger.focus();
  }

  function close(openDrawer) {
    var el = modal();
    if (!el) return;
    el.hidden = true;

    if (checkoutPending) {
      var btn = checkoutPending;
      checkoutPending = null;
      releaseDrawer(false);
      staged = [];
      var p0 = picker();
      if (p0) p0.classList.remove('open');
      render();
      proceed(btn);
      return;
    }

    releaseDrawer(openDrawer !== false);
    staged = [];
    var p = picker();
    if (p) p.classList.remove('open');
    render();
  }

  function render() {
    var el = modal();
    if (!el) return;

    var host = el.querySelector('[data-gpo-staged]');
    if (host) {
      host.innerHTML = staged
        .map(function (s, i) {
          return (
            '<div class="pair-staged-row">' +
            '<div class="pair-staged-info"><div class="pair-staged-name">' +
            s.name +
            '</div></div>' +
            '<div class="pair-staged-controls"><div class="pair-staged-controls-top">' +
            '<div class="pair-staged-price">' +
            money(s.price * s.qty) +
            '</div>' +
            '<div class="pair-staged-qty">' +
            '<button type="button" data-gpo-qty="-1" data-i="' + i + '">&ndash;</button>' +
            '<span>' + s.qty + '</span>' +
            '<button type="button" data-gpo-qty="1" data-i="' + i + '">+</button>' +
            '</div></div>' +
            '<button type="button" class="pair-staged-remove" data-gpo-unstage data-i="' + i + '">Change</button>' +
            '</div></div>'
          );
        })
        .join('');
    }

    var label = el.querySelector('[data-gpo-label]');
    if (label) {
      label.textContent = staged.length
        ? label.dataset.more || 'Add another'
        : label.dataset.placeholder || label.textContent;
    }

    var p = picker();
    if (p) p.classList.toggle('chosen', staged.length > 0);

    // The helper line stops asking once something is chosen, and reports instead.
    var status = el.querySelector('[data-gpo-status]');
    if (status) {
      if (!status.dataset.help) status.dataset.help = status.innerHTML;
      if (!staged.length) {
        status.innerHTML = status.dataset.help;
      } else {
        var total = staged.reduce(function (n, s) {
          return n + s.price * s.qty;
        }, 0);
        status.innerHTML = 'Adding <b>' + money(total) + '</b> to your bag.';
      }
    }

    var cta = el.querySelector('[data-gpo-add]');
    if (cta) {
      cta.disabled = staged.length === 0;
      // Each offer names its own button ("Add Pairing + Save $5", "Add My Gift"),
      // so the label is read back from the markup instead of hard-coded here.
      if (!cta.dataset.gpoLabel) cta.dataset.gpoLabel = cta.textContent.trim() || 'Add to Bag';
      cta.textContent = cta.dataset.gpoLabel;
    }
  }

  function stage(option) {
    var id = option.getAttribute('data-variant-id');
    var existing = staged.filter(function (s) {
      return s.variantId === id;
    })[0];
    if (existing) {
      existing.qty += 1;
    } else {
      staged.push({
        variantId: id,
        name: option.getAttribute('data-name') || '',
        price: Number(option.getAttribute('data-price')) || 0,
        qty: 1,
      });
    }
    var p = picker();
    if (p) {
      p.classList.remove('open');
      var trigger = p.querySelector('[data-gpo-toggle]');
      if (trigger) trigger.setAttribute('aria-expanded', 'false');
    }
    render();
  }

  function commit(cta) {
    if (!staged.length) return;
    cta.disabled = true;
    cta.textContent = 'Adding…';

    var items = staged.map(function (s) {
      return { id: Number(s.variantId), quantity: s.qty };
    });

    fetch((window.routes && window.routes.cart_add_url ? window.routes.cart_add_url : '/cart/add') + '.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/javascript' },
      body: JSON.stringify({ items: items, sections: SECTIONS, sections_url: window.location.pathname }),
    })
      .then(function (response) {
        return response.json();
      })
      .then(function (parsed) {
        if (parsed.status) {
          cta.disabled = false;
          cta.textContent = parsed.description || parsed.message || 'Unavailable';
          return;
        }
        // Let the drawer open off the render below, not twice.
        close(false);
        remember(parsed);
        applyCartResponse(parsed);
      })
      .catch(function () {
        cta.disabled = false;
        cta.textContent = 'Add to Bag';
      });
  }

  /* ---- wiring ----------------------------------------------------------- */

  document.addEventListener('click', function (event) {
    if (event.target.closest('[data-gpo-close]')) {
      event.preventDefault();
      close(true);
      return;
    }

    // Clicking the ground outside the panel is the same answer as the close
    // mark, whether it lands on the scrim or on the dialog's own padding.
    if (event.target === modal()) {
      event.preventDefault();
      close(true);
      return;
    }

    if (event.target.closest('[data-gpo-toggle]')) {
      event.preventDefault();
      var p = picker();
      if (!p) return;
      var isOpen = p.classList.toggle('open');
      var trigger = p.querySelector('[data-gpo-toggle]');
      if (trigger) trigger.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
      return;
    }

    var option = event.target.closest('[data-gpo-pick]');
    if (option) {
      event.preventDefault();
      stage(option);
      return;
    }

    var step = event.target.closest('[data-gpo-qty]');
    if (step) {
      event.preventDefault();
      var row = staged[Number(step.getAttribute('data-i'))];
      if (row) {
        row.qty += Number(step.getAttribute('data-gpo-qty'));
        if (row.qty < 1) staged.splice(Number(step.getAttribute('data-i')), 1);
        render();
      }
      return;
    }

    var drop = event.target.closest('[data-gpo-unstage]');
    if (drop) {
      event.preventDefault();
      staged.splice(Number(drop.getAttribute('data-i')), 1);
      render();
      return;
    }

    var cta = event.target.closest('[data-gpo-add]');
    if (cta && !cta.disabled) {
      event.preventDefault();
      commit(cta);
      return;
    }

    var pick = picker();
    if (pick && pick.classList.contains('open') && !pick.contains(event.target)) {
      pick.classList.remove('open');
    }
  });

  document.addEventListener('keydown', function (event) {
    var el = modal();
    if (event.key === 'Escape' && el && !el.hidden) {
      event.stopPropagation();
      close(true);
    }
  });

  /* ---- when to ask ------------------------------------------------------
     The mockups do NOT interrupt Add to Bag. The dialog belongs to the cart
     drawer: the reward is claimed from its own link, and the pairing / dozen
     offer is the last word before checkout. A block says which moment it
     wants; "checkout" is the default because that is what the mockups do. */
  function listen() {
    if (!sub || !EVENTS) return false;
    sub(EVENTS.cartUpdate, function (event) {
      if (!event || event.source === 'giorgio-pairing-offer') return;
      maybeOffer(event.cartData, 'add_to_cart');
    });
    return true;
  }

  // Checkout is a form submit inside the drawer, so the click is caught in the
  // capture phase and held only long enough to read the cart and decide.
  document.addEventListener(
    'click',
    function (event) {
      var btn = event.target.closest && event.target.closest(
        '#CartDrawer-Checkout, [name="checkout"], .drawer-checkout, .cart__checkout-button'
      );
      if (!btn || btn.disabled || asked) return;
      if (!allOffers().length) return;

      event.preventDefault();
      event.stopPropagation();

      fetch((window.routes && window.routes.cart_url ? window.routes.cart_url : '/cart') + '.js')
        .then(function (r) { return r.json(); })
        .then(function (cart) {
          /* The shopper pressed Checkout. If the offer opens, that intent is
             kept, and CLOSING the offer — decline, scrim, escape, or adding
             the pairing — carries on to checkout instead of dropping them
             back into the drawer to press the same button again, which read
             as checkout simply not working. */
          if (maybeOffer(cart, 'checkout')) checkoutPending = btn;
          else proceed(btn);
        })
        .catch(function () { proceed(btn); });
    },
    true
  );

  // Nothing to offer (or the shopper declined): let checkout happen for real.
  function proceed(btn) {
    checkoutPending = null;
    var form = btn.form || btn.closest('form');
    if (form) {
      /* form.submit() does not include the button's own name=checkout, and a
         POST to /cart without it just re-renders the cart page. */
      if (!form.querySelector('input[name="checkout"]')) {
        var hidden = document.createElement('input');
        hidden.type = 'hidden';
        hidden.name = 'checkout';
        hidden.value = '';
        form.appendChild(hidden);
      }
      form.submit();
    } else if (btn.href) {
      window.location.href = btn.href;
    }
  }

  if (!listen()) {
    document.addEventListener('DOMContentLoaded', listen);
  }
})();